import { useContext, useEffect } from "react"
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import Header from "./components/common/Drawer"
import Loading from "./components/common/Loading"
import BaseLayout from "./components/layout/BaseLayout"
import Dashboard from "./pages/Dashboard/pages/Dashboard"
import ErrorPage from "./pages/404/ErrorPage"
import PaymentPage from "./pages/Buchhaltung/pages/PaymentPage"
import Mitarbeiter from "./pages/Mitarbeiter/pages/Mitarbeiter"
import Objekte from "./pages/Objekte/pages/Objekte"
import "./style/scroll.css"
import Kunden from "./pages/Kunden/pages/Kunden"
import OfferPage from "./pages/Buchhaltung/pages/OfferPage"
import OfferDetailPage from "./pages/Buchhaltung/pages/OfferDetailPage"
import CustomerDetailPage from "./pages/Kunden/pages/CustomerDetailPage"
import ObjektDetailPage from "./pages/Objekte/pages/objectDetail/ObjektDetailPage"
import Hilfe from "./pages/Hilfe/pages/Hilfe"
import BillPage from "./pages/Buchhaltung/pages/BillPage"
import { P } from "./auth/permissions"
import { Authorized } from "./components/authentication/Authorized"
import DownloadPage from "./components/common/DownloadPage"
import TaskOverviewPage from "./pages/Aufgaben/pages/TaskOverviewPage"
import CalendarPage from "./pages/Aufgaben/pages/CalendarPage"
import TaskDetailPage from "./pages/Aufgaben/pages/TaskDetailPage"
import TaskTemplateOverviewPage from "./pages/Aufgaben/pages/TaskTemplateOverviewPage"
import TaskTemplateDetailPage from "./pages/Aufgaben/pages/TaskTemplateDetailPage"
import SessionDetailPage from "./pages/Aufgaben/pages/SessionDetailPage"
import TourenPage from "./pages/Touren/pages/TourenPage"
import TourDetailPage from "./pages/Touren/pages/TourDetailPage"
import { SelfUserContext } from "./components/contexts/SelfUserContext"
import MitarbeiterDetailPage from "./pages/Mitarbeiter/pages/MitarbeiterDetailPage"
import OrderRequestPage from "./pages/Anfragen/pages/OrderRequestPage"
import OrderRequestDetailPage from "./pages/Anfragen/pages/OrderRequestDetailPage"
import BillTemplatePage from "./pages/Buchhaltung/pages/BillTemplatePage"
import BillTemplateDetailPage from "./pages/Buchhaltung/pages/BillTemplateDetailPage"
import MajorOrderPage from "./pages/Buchhaltung/pages/MajorOrderPage"
import MajorOrderDetailPage from "./pages/Buchhaltung/pages/MajorOrderDetailPage"
import KartePage from "./pages/Karte/pages/KartePage"
import { GeneralQueryKeys, Navigation } from "./api/constants"
import BillDetailPage from "./pages/Buchhaltung/pages/BillDetailPage"
import AbsencePage from "./pages/Abwesenheiten/pages/AbsencePage"
import TransactionsPage from "./pages/Buchhaltung/pages/Transactions"
import Overview from "./pages/Übersicht/pages/Overview"
import ComingSoon from "./pages/404/ComingSoon"
import SettingsPage from "./pages/Einstellungen/pages/SettingsPage"
import { getColorAndLogo, getLogo } from "./pages/Einstellungen/api/apiCalls"
import { useQuery } from "@tanstack/react-query"
import { hexToHsl } from "./services/functions"
import AddReport from "./pages/Aufgaben/pages/AddReport"
import ReportsPage from "./pages/Berichte/pages/ReportsPage"
import ReportDetailPage from "./pages/Berichte/pages/ReportDetailPage"
import KeyPage from "./pages/Schlüssel/pages/KeyPage"
import CreditPage from "./pages/Buchhaltung/pages/CreditPage"
import Login from "./pages/Login/pages/Login"
import AccountIncomplete from "./pages/Login/pages/AccountIncomplete"
import PasswordForgot from "./pages/Passwort/pages/PasswordForgot"
import PasswordSet from "./pages/Passwort/pages/PasswordSet"
import { useAuth } from "./components/contexts/AuthContext"
import { loginPathFor, safeRedirect } from "./auth/redirect"

// Passwort-Seiten sind öffentlich und auch mit bestehender Anmeldung erreichbar (Einladungslink in einem Browser, in dem schon jemand angemeldet ist).
// Sie werden vor der Auth-Weiche gerendert: ohne Warten auf /auth/session, ohne Umleitung über /login?redirect= (der Token im Fragment würde sonst im Query-String landen)
// und ohne die Effekte der angemeldeten App (z. B. Weiterleitung auf eine laufende Session).
const PASSWORD_PATHS = ["/passwort/vergessen", "/passwort/neu"]

function App() {
    const auth = useAuth()
    const location = useLocation()
    if (PASSWORD_PATHS.includes(location.pathname.replace(/\/+$/, ""))) return <PasswordRoutes />
    if (auth.isLoading)
        return (
            <main className="relative overflow-hidden w-screen h-screen">
                <Loading />
            </main>
        )
    if (!auth.isAuthenticated) return <PublicApp />
    if (auth.accountIncomplete) return <AccountIncomplete />
    return <AuthenticatedApp />
}

function PasswordRoutes() {
    return (
        <Routes>
            <Route path="/passwort/vergessen" element={<PasswordForgot />} />
            <Route path="/passwort/neu" element={<PasswordSet />} />
        </Routes>
    )
}

// ohne Anmeldung: nur öffentliche Seiten, alles andere -> /login?redirect=<pfad>
function PublicApp() {
    const location = useLocation()
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate replace to={loginPathFor(location.pathname, location.search, location.hash)} />} />
        </Routes>
    )
}

// nach der Anmeldung zurück auf die ursprünglich angefragte Seite
function LoginRedirect() {
    const [searchParams] = useSearchParams()
    return <Navigate replace to={safeRedirect(searchParams.get("redirect"))} />
}

function AuthenticatedApp() {
    // get current user
    const userSelf = useContext(SelfUserContext)
    const { data: colorAndLogo, isLoading: companyDataLoading } = useQuery({
        queryKey: [GeneralQueryKeys.COMPANY_DATA],
        queryFn: () => getColorAndLogo(),
    })

    const { data: logo, isLoading: logoLoading } = useQuery({
        queryKey: [GeneralQueryKeys.COMPANY_DATA_LOGO],
        queryFn: () => getLogo(),
        enabled: !!colorAndLogo?.logo?.s3Key,
    })

    useEffect(() => {
        if (colorAndLogo?.color) {
            document.documentElement.style.setProperty("--p", hexToHsl(`${colorAndLogo?.color}`))
            document.documentElement.style.setProperty("--p-hex", `${colorAndLogo?.color}`)
            document.documentElement.style.setProperty("--p-hex-50", `${colorAndLogo?.color}50`)
        }
    }, [colorAndLogo])

    // navigate to current user session, if user has an ongoing session
    const navigate = useNavigate()
    useEffect(() => {
        if (userSelf?.user?.activeSession) navigate(Navigation.SESSION + userSelf?.user?.activeSession)
    }, [userSelf?.user?.activeSession])

    return (
        <main className="relative overflow-hidden">
            {(userSelf?.isLoading || companyDataLoading) && <Loading />}
            {!userSelf?.isLoading && !companyDataLoading && (
                <div className={"flex flex-row w-screen min-h-screen bg-base-100 overflow-hidden"}>
                    <Header logo={logo} />
                    {/* ROUTES */}
                    <div className="flex flex-col min-h-full grow w-full overflow-hidden">
                        <Routes>
                            {/* CalendarDetailView of Employee */}
                            <Route
                                path="/kalender/:id"
                                element={
                                    <Authorized page allOf={[]}>
                                        <BaseLayout permissions={[]} title="Terminkalender" image={logo ? logo as string : "/hausmanager-logo.png"} children={<CalendarPage />} />
                                    </Authorized>
                                }
                            />
                            <Route
                                path="/"
                                element={
                                    <Authorized page allOf={[]}>
                                        <Navigate to={Navigation.CALENDAR + userSelf?.user?._id} />
                                    </Authorized>
                                }
                            />
                            <Route path="/login" element={<LoginRedirect />} />
                            {/* Dashboard Routes */}
                            <Route
                                path={Navigation.DASHBOARD}
                                element={
                                    <Authorized page allOf={[P.dashboard_read]}>
                                        <BaseLayout title="Dashboard" permissions={[P.dashboard_read]} image={logo ? logo as string : "/hausmanager-logo.png"} children={<Dashboard />} />
                                    </Authorized>
                                }
                            />
                            {/* Payment Requirements Routes */}
                            <Route
                                path={Navigation.PAY_REQ}
                                element={
                                    <Authorized page allOf={[P.buchhaltung_zahlungen_read]}>
                                        <BaseLayout permissions={[P.buchhaltung_read]} title="Zahlungen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<PaymentPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Transactions Routes */}
                            <Route
                                path={Navigation.TRANSACTIONS}
                                element={
                                    <Authorized page allOf={[P.buchhaltung_zahlungen_read]}>
                                        <BaseLayout permissions={[P.buchhaltung_read]} title="Transaktionen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<TransactionsPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Bill Route */}
                            <Route
                                path={Navigation.BILL}
                                element={
                                    <Authorized page anyOf={[P.buchhaltung_read, P.buchhaltung_read_restricted]}>
                                        <BaseLayout permissions={[P.buchhaltung_read, P.buchhaltung_read_restricted]} title="Rechnungen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<BillPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Bill Template Route */}
                            <Route
                                path={Navigation.BILL_TEMPLATE}
                                element={
                                    <Authorized page anyOf={[P.buchhaltung_read, P.buchhaltung_read_restricted]}>
                                        <BaseLayout permissions={[P.buchhaltung_read, P.buchhaltung_read_restricted]} title="Rechnungsvorlagen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<BillTemplatePage />} />
                                    </Authorized>
                                }
                            />
                            {/* Major Order Route */}
                            <Route
                                path={Navigation.MAJOR_ORDER}
                                element={
                                    <Authorized page anyOf={[P.buchhaltung_read, P.buchhaltung_read_restricted]}>
                                        <BaseLayout permissions={[P.buchhaltung_read, P.buchhaltung_read_restricted]} title="Aufträge" image={logo ? logo as string : "/hausmanager-logo.png"} children={<MajorOrderPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Offer Route */}
                            <Route
                                path={Navigation.OFFER}
                                element={
                                    <Authorized page anyOf={[P.buchhaltung_read, P.buchhaltung_read_restricted]}>
                                        <BaseLayout permissions={[P.buchhaltung_read, P.buchhaltung_read_restricted]} title="Angebote" image={logo ? logo as string : "/hausmanager-logo.png"} children={<OfferPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Absence Route */}
                            <Route
                                path={Navigation.ABSENCE}
                                element={
                                    <Authorized page allOf={[P.abwesenheiten_read]}>
                                        <BaseLayout permissions={[P.abwesenheiten_read]} title="Abwesenheiten" image={logo ? logo as string : "/hausmanager-logo.png"} children={<AbsencePage />} />
                                    </Authorized>
                                }
                            />
                            {/* Bill Detailed Route */}
                            <Route
                                path="/buchhaltung/rechnung/:id"
                                element={
                                    <Authorized page allOf={[P.buchhaltung_read]}>
                                        <BaseLayout permissions={[P.buchhaltung_read]} title="Rechnungen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<BillDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Major Order Detailed Route */}
                            <Route
                                path="/buchhaltung/aufträge/:id"
                                element={
                                    <Authorized page allOf={[P.buchhaltung_read]}>
                                        <BaseLayout permissions={[P.buchhaltung_read]} title="Rechnungen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<MajorOrderDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Bill Template Detailed Route */}
                            <Route
                                path="/buchhaltung/rechnungsvorlagen/:id"
                                element={
                                    <Authorized page allOf={[P.buchhaltung_read]}>
                                        <BaseLayout permissions={[P.buchhaltung_read]} title="Rechnungsvorlagen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<BillTemplateDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Offer Detailed Routes */}
                            <Route
                                path="/buchhaltung/angebote/:id"
                                element={
                                    <Authorized page allOf={[P.buchhaltung_read]}>
                                        <BaseLayout permissions={[P.buchhaltung_read]} title="Angebot" image={logo ? logo as string : "/hausmanager-logo.png"} children={<OfferDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Credit Routes */}
                            <Route
                                path="/buchhaltung/gutschriften"
                                element={
                                    <Authorized page allOf={[P.buchhaltung_read]}>
                                        <BaseLayout permissions={[P.buchhaltung_read]} title="Gutschriften" image={logo ? logo as string : "/hausmanager-logo.png"} children={<CreditPage />} />
                                    </Authorized>
                                }
                            />
                            <Route
                                path={Navigation.TOURS}
                                element={
                                    <Authorized page anyOf={[P.termine_execute, P.termine_management]}>
                                        <BaseLayout permissions={[]} title="Touren" image={logo ? logo as string : "/hausmanager-logo.png"} children={<TourenPage />} />
                                    </Authorized>
                                }
                            />
                            <Route
                                path="/touren/:id"
                                element={
                                    <Authorized page anyOf={[P.termine_execute, P.termine_management]}>
                                        <BaseLayout permissions={[]} title="Tour" image={logo ? logo as string : "/hausmanager-logo.png"} children={<TourDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Session Detail View */}
                            <Route
                                path="/termin/:id"
                                element={
                                    <Authorized page allOf={[]}>
                                        <BaseLayout permissions={[]} title="Termin" image={logo ? logo as string : "/hausmanager-logo.png"} children={<SessionDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Aufgaben Routes */}
                            <Route
                                path={Navigation.TASKS}
                                element={
                                    <Authorized page allOf={[P.aufgaben_read, P.aufgaben_create, P.aufgaben_delete]}>
                                        <BaseLayout permissions={[]} title="Aufträge" image={logo ? logo as string : "/hausmanager-logo.png"} children={<TaskOverviewPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Single Task View */}
                            <Route
                                path="/aufgaben/:id"
                                element={
                                    <Authorized page allOf={[P.aufgaben_read, P.aufgaben_read_write, P.aufgaben_create]}>
                                        <BaseLayout permissions={[]} title="Aufgabe" image={logo ? logo as string : "/hausmanager-logo.png"} children={<TaskDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Bericht erstellen */}
                            <Route
                                path={Navigation.ADD_REPORT}
                                element={
                                    <Authorized page allOf={[P.aufgaben_read, P.aufgaben_read_write, P.aufgaben_create]}>
                                        <BaseLayout permissions={[]} title="Bericht erstellen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<AddReport />} />
                                    </Authorized>
                                }
                            />
                            {/* Berichte Routes */}
                            <Route
                                path={Navigation.REPORTS}
                                element={
                                    <Authorized page allOf={[P.aufgaben_read, P.aufgaben_read_write, P.aufgaben_create]}>
                                        <BaseLayout permissions={[]} title="Berichte" image={logo ? logo as string : "/hausmanager-logo.png"} children={<ReportsPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Single Report View */}
                            <Route
                                path="/berichte/:id"
                                element={
                                    <Authorized page allOf={[P.aufgaben_read, P.aufgaben_read_write, P.aufgaben_create]}>
                                        <BaseLayout permissions={[]} title="Bericht" image={logo ? logo as string : "/hausmanager-logo.png"} children={<ReportDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* TaskTemplates  */}
                            <Route
                                path={Navigation.TASK_TEMPLATES}
                                element={
                                    <Authorized page allOf={[P.aufgaben_read, P.aufgaben_create, P.aufgaben_delete]}>
                                        <BaseLayout permissions={[]} title="Vorlagen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<TaskTemplateOverviewPage />} />
                                    </Authorized>
                                }
                            />
                            {/* TaskTemplates Detail  */}
                            <Route
                                path="/aufgaben-wiederkehrend/:id"
                                element={
                                    <Authorized page allOf={[P.aufgaben_read, P.aufgaben_create, P.aufgaben_delete]}>
                                        <BaseLayout permissions={[]} title="Vorlage" image={logo ? logo as string : "/hausmanager-logo.png"} children={<TaskTemplateDetailPage />} />
                                    </Authorized>
                                }
                            />

                            {/* Mitarbeiter Routes */}
                            <Route
                                path={Navigation.EMPLOYEE}
                                element={
                                    <Authorized page allOf={[P.mitarbeiter_read, P.mitarbeiter_read_ou]}>
                                        <BaseLayout permissions={[P.mitarbeiter_read]} title="Mitarbeiter" image={logo ? logo as string : "/hausmanager-logo.png"} children={<Mitarbeiter />} />
                                    </Authorized>
                                }
                            />
                            {/* Übersicht Routes */}
                            <Route
                                path={Navigation.OVERVIEW}
                                element={
                                    <Authorized page allOf={[P.mitarbeiter_read, P.mitarbeiter_read_ou]}>
                                        <BaseLayout permissions={[P.mitarbeiter_read]} title="Mitarbeiter Übersicht" image={logo ? logo as string : "/hausmanager-logo.png"} children={<Overview />} />
                                    </Authorized>
                                }
                            />
                            {/* EmploeeId Route */}
                            <Route
                                path="/mitarbeiter/:id"
                                element={
                                    <Authorized page allOf={[P.mitarbeiter_read, P.mitarbeiter_read_ou, P.mitarbeiter_read_write]}>
                                        <BaseLayout permissions={[P.mitarbeiter_read]} title="Mitarbeiter" image={logo ? logo as string : "/hausmanager-logo.png"} children={<MitarbeiterDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Object Route */}
                            <Route
                                path={Navigation.OBJECTS}
                                element={
                                    <Authorized page allOf={[P.objekte_read]}>
                                        <BaseLayout permissions={[P.objekte_read]} title="Objekte" image={logo ? logo as string : "/hausmanager-logo.png"} children={<Objekte />} />
                                    </Authorized>
                                }
                            />
                            {/* Object Detailed Route */}
                            <Route
                                path="/objekte/:id"
                                element={
                                    <Authorized page allOf={[P.objekte_read]}>
                                        <BaseLayout permissions={[P.objekte_read]} title="Objekte" image={logo ? logo as string : "/hausmanager-logo.png"} children={<ObjektDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Key Route */}
                            <Route
                                path={Navigation.KEYS}
                                element={
                                    <Authorized page allOf={[P.objekte_read]}>
                                        <BaseLayout permissions={[P.objekte_read]} title="Schlüssel" image={logo ? logo as string : "/hausmanager-logo.png"} children={<KeyPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Customer Route */}
                            <Route
                                path={Navigation.CUSTOMER}
                                element={
                                    <Authorized page allOf={[P.kunden_read]}>
                                        <BaseLayout permissions={[P.kunden_read]} title="Kunden" image={logo ? logo as string : "/hausmanager-logo.png"} children={<Kunden />} />
                                    </Authorized>
                                }
                            />
                            {/* Customer Detailed Route */}
                            <Route
                                path="/kunden/:id"
                                element={
                                    <Authorized page allOf={[P.kunden_read]}>
                                        <BaseLayout permissions={[P.kunden_read]} title="Kunden" image={logo ? logo as string : "/hausmanager-logo.png"} children={<CustomerDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Order Request Route */}
                            <Route
                                path={Navigation.ORDER_REQUESTS}
                                element={
                                    <Authorized page allOf={[P.anfragen]}>
                                        <BaseLayout permissions={[]} title="Anfragen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<OrderRequestPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Order Request Detail Route */}
                            <Route
                                path="/anfragen/:id"
                                element={
                                    <Authorized page allOf={[P.anfragen]}>
                                        <BaseLayout permissions={[]} title="Anfrage" image={logo ? logo as string : "/hausmanager-logo.png"} children={<OrderRequestDetailPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Map Section */}
                            {/* TODO protect, je nach Wunsch -> wenn nur NL/Admin Zugriff neue Karten-Rolle anlegen */}
                            <Route
                                path={Navigation.MAP}
                                element={
                                    <Authorized page allOf={[P.objekte_read]}>
                                        <BaseLayout permissions={[P.objekte_read]} title="Karte" image={logo ? logo as string : "/hausmanager-logo.png"} children={<KartePage />} />
                                    </Authorized>
                                }
                            />
                            {/* Help Section */}
                            <Route
                                path={Navigation.HELP}
                                element={
                                    <Authorized page allOf={[P.help_read_write]}>
                                        <BaseLayout permissions={[P.help_read_write]} title="Hilfe" image={logo ? logo as string : "/hausmanager-logo.png"} children={<Hilfe />} />
                                    </Authorized>
                                }
                            />
                            {/* Download Route for ZIP */}
                            <Route
                                path="/export/download-zip"
                                element={
                                    <Authorized page allOf={[P.objekte_read_write]}>
                                        <BaseLayout permissions={[P.objekte_read_write]} title="Download..." image={logo ? logo as string : "/hausmanager-logo.png"} children={<DownloadPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Settings Route */}
                            <Route
                                path="/einstellungen"
                                element={
                                    <Authorized page allOf={[P.global_settings_read_write]}>
                                        <BaseLayout permissions={[P.global_settings_read_write]} title="Einstellungen" image={logo ? logo as string : "/hausmanager-logo.png"} children={<SettingsPage />} />
                                    </Authorized>
                                }
                            />
                            {/* Dateimanagement Route */}
                            <Route
                                path="/dateimanagement"
                                element={
                                    <Authorized page allOf={[]}>
                                        <Navigate to={Navigation.COMING_SOON} />
                                    </Authorized>
                                }
                            />
                            {/* Fuhrpark Route */}
                            <Route
                                path="/fuhrpark"
                                element={
                                    <Authorized page allOf={[]}>
                                        <Navigate to={Navigation.COMING_SOON} />
                                    </Authorized>
                                }
                            />
                            {/* Telefonassistent Route */}
                            <Route
                                path="/telefonassistent"
                                element={
                                    <Authorized page allOf={[]}>
                                        <Navigate to={Navigation.COMING_SOON} />
                                    </Authorized>
                                }
                            />
                            {/* Errorpage */}
                            <Route path={Navigation.ERROR} element={<BaseLayout permissions={[]} title="Page Not Found" image={logo ? logo as string : "/hausmanager-logo.png"} children={<ErrorPage />} />} />
                            <Route path={Navigation.COMING_SOON} element={<BaseLayout permissions={[]} title="Coming Soon" image={logo ? logo as string : "/hausmanager-logo.png"} children={<ComingSoon />} />} />
                            <Route path="*" element={<Navigate to="/404" />} />
                        </Routes>
                    </div>
                </div>
            )}
        </main>
    )

}

export default App
